// The reader's recent emoji: ONE list for the picker's "Recientes" row, the hover bar's quick
// reactions and the message menu's quick row (Apple: `@AppStorage(QuickReactions.recentsKey)`
// written by `EmojiPickerSheet.remember`, read by `HoverQuickReactions`). Picking in the picker
// therefore feeds the hover bar, and a quick reaction moves to the front of the picker.
//
// Per-viewer convenience in localStorage (never reliable state): every access is guarded, and a
// browser that refuses storage just keeps the list for the session.
import { useMemo, useSyncExternalStore } from 'react';
import { EmojiPicker } from '../../../core/emojiPicker';
import { QuickReactions } from '../../../core/quickReactions';

const KEY = QuickReactions.recentsKey;
/** What the first W2 hover bar wrote; folded into KEY once, then removed. */
const LEGACY_KEY = 'quick_reactions_recents';

const listeners = new Set<() => void>();
let cache: string | null = null;

function load(): string {
  let raw = '';
  try {
    raw = localStorage.getItem(KEY) ?? '';
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy !== null) {
      const merged = [...new Set([...legacy.split(','), ...raw.split(',')].filter(Boolean))].slice(0, EmojiPicker.recentsLimit).join(',');
      raw = merged;
      localStorage.setItem(KEY, merged);
      localStorage.removeItem(LEGACY_KEY);
    }
  } catch {
    /* storage refused: start empty */
  }
  return raw;
}

export function recentsRaw(): string {
  if (cache === null) cache = load();
  return cache;
}

/** A pick (picker, hover bar, menu, chip): to the front of the shared list. */
export function rememberEmoji(name: string): void {
  cache = EmojiPicker.remember(recentsRaw(), name);
  try {
    localStorage.setItem(KEY, cache);
  } catch {
    /* kept in memory for this session */
  }
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useRecentsRaw(): string {
  return useSyncExternalStore(subscribe, recentsRaw, recentsRaw);
}

/** The quick-reaction row: recents first, then Android's defaults (QuickReactions.names). */
export function useQuickReactionNames(limit = 6): string[] {
  const raw = useRecentsRaw();
  return useMemo(() => QuickReactions.names(raw, limit, (n) => EmojiPicker.glyph(n) != null), [raw, limit]);
}
